import { Box } from "@mui/material";
import { sumBy } from "@sinch/utils/array";
import { flatten, pluck } from "ramda";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  CalculatedRateItems,
  ComponentRow,
} from "@sinch/components/application/RateComponentsMarginTable/RateComponentsMargin";

/**
 * Builds rows from rate components by merging price and wage data
 */
export const useBuildRows = (totalRow?: boolean) => {
  const { t } = useTranslation(["common", "draft"]);

  return useCallback(
    (
      priceCalculations: CalculatedRateItems[] | undefined | null,
      wageCalculations: CalculatedRateItems[] | undefined | null,
      parent: string[] = []
    ): ComponentRow[] => {
      const priceComponents = flatten(pluck("components", priceCalculations ?? []));
      const wageComponents = flatten(pluck("components", wageCalculations ?? []));

      const componentMap = new Map<
        string,
        { priceDetails: CalculatedRateItems["components"][0][]; wageDetails: CalculatedRateItems["components"][0][] }
      >();

      // Group price components
      priceComponents.forEach((rate) => {
        const entry = componentMap.get(rate.component) ?? { priceDetails: [], wageDetails: [] };
        entry.priceDetails.push(rate);
        componentMap.set(rate.component, entry);
      });

      // Group wage components
      wageComponents.forEach((rate) => {
        const entry = componentMap.get(rate.component) ?? { priceDetails: [], wageDetails: [] };
        entry.wageDetails.push(rate);
        componentMap.set(rate.component, entry);
      });

      // Build row array from grouped data
      const rows: ComponentRow[] = Array.from(componentMap.entries()).map(
        ([component, { priceDetails, wageDetails }]) => {
          const price = priceDetails.reduce((sum, item) => sum + item.total, 0);
          const wage = wageDetails.reduce((sum, item) => sum + item.total, 0);

          return {
            id: parent.join(":") + ":" + component,
            path: [...parent, component],
            label: component,
            component,
            price,
            wage,
            total: price - wage,
            priceOverview: priceDetails,
            wageOverview: wageDetails,
            wageDetails: [],
            priceDetails: [],
          };
        }
      );

      // Sort alphabetically by component name
      rows.sort((a, b) => a.component.localeCompare(b.component));

      if (totalRow) {
        rows.push({
          id: "__total__",
          path: [...parent, "__total__"],
          label: (
            <Box
              sx={(theme) => ({
                width: "100%",
                fontWeight: theme.typography.fontWeightBold,
              })}
            >
              {t("total")}
            </Box>
          ),
          component: t("total"),
          price: sumBy("total", priceCalculations ?? []),
          wage: sumBy("total", wageCalculations ?? []),
          total: sumBy("total", priceCalculations ?? []) - sumBy("total", wageCalculations ?? []),
          priceOverview: [],
          wageOverview: [],
          wageDetails: [],
          priceDetails: [],
        });
      }

      return rows;
    },
    [t]
  );
};
